/**
 * Color Switcher Block Frontend Script
 *
 * Handles the color palette switching functionality using portal_global data
 * and direct CSS variable updates for optimal performance.
 *
 * @package
 * @since x.x.x
 */

( function() {
	'use strict';

	/**
	 * SureDash Color Switcher Class
	 */
	const SureDashColorSwitcher = {

		// Configuration.
		isInit: false,
		switcherButtons: null,
		defaultMode: portal_global?.default_palette || 'light',
		forceReload: false,
		isSwitched: false,
		currentPalette: 'light',

		/**
		 * Initialize the Color Switcher
		 */
		init() {
			if ( window.document.body.classList.contains( 'wp-admin' ) ) {
				return;
			}

			if ( this.isInit ) {
				return;
			}

			this.switcherButtons = document.querySelectorAll( '.suredash-color-switcher .switcher-button' );

			if ( ! this.switcherButtons?.length ) {
				return;
			}

			// Check if we have valid palette data and if active_palette is light or dark
			if ( typeof portal_global === 'undefined' ||
				! portal_global.color_palette ||
				! portal_global.default_palette ||
				( portal_global.default_palette !== 'light' && portal_global.default_palette !== 'dark' ) ) {
				return;
			}

			// Mark as initialized.
			this.isInit = true;

			// Set up event listeners.
			this.switcherButtons.forEach( ( button ) => {
				const wrapper = button.closest( '.suredash-color-switcher-wrapper' );
				if ( wrapper ) {
					this.defaultMode = wrapper.dataset.defaultMode || 'light';
					this.forceReload = wrapper.dataset.forceReload === 'true';
				}

				button.addEventListener( 'click', this.toggle.bind( this ) );
			} );

			// Initialize current state.
			this.initializeState();

			// Handle system preference detection && default mode.
			this.detectSystemColorScheme();

			// Listen for system color scheme changes.
			if ( window.matchMedia ) {
				window.matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', ( e ) => {
					if ( this.defaultMode === 'system' && this.getCookie( 'suredashColorSwitcherState' ) === null ) {
						this.isSwitched = e.matches;
						this.currentPalette = e.matches ? 'dark' : 'light';
						this.updateUI();
						this.updateCSSVariables();
					}
				} );
			}
		},

		/**
		 * Initialize the current state based on cookies and global data.
		 */
		initializeState() {
			const storedState = this.getCookie( 'suredashColorSwitcherState' );

			if ( storedState !== null ) {
				this.isSwitched = ( storedState === 'true' );
				this.currentPalette = this.isSwitched ? 'dark' : 'light';
			} else {
				// Get current palette from portal_global data.
				if ( typeof portal_global !== 'undefined' && // eslint-disable-line no-lonely-if
					portal_global.color_palette &&
					portal_global.default_palette &&
					( portal_global.default_palette === 'light' || portal_global.default_palette === 'dark' ) ) {
					this.currentPalette = portal_global.default_palette;
					this.isSwitched = ( this.currentPalette === 'dark' );
				} else {
					// Fallback to light mode if no valid palette is found
					this.currentPalette = 'light';
					this.isSwitched = false;
				}
			}

			this.updateUI();
			this.updateCSSVariables();
		},

		/**
		 * Detect system color scheme preference
		 */
		detectSystemColorScheme() {
			const storedPreference = this.getCookie( 'suredashColorSwitcherState' );

			// Don't override user's explicit choice.
			if ( storedPreference !== null ) {
				return;
			}

			// Detect system preference.
			const systemPreference = this.defaultMode === 'system' ? window.matchMedia && window.matchMedia( '(prefers-color-scheme: dark)' ).matches : false;

			if ( systemPreference ) {
				if ( systemPreference && ! this.isSwitched ) {
					this.isSwitched = true;
					this.currentPalette = 'dark';
					this.updateUI();
					this.updateCSSVariables();
				} else if ( ! systemPreference && this.isSwitched ) {
					this.isSwitched = false;
					this.currentPalette = 'light';
					this.updateUI();
					this.updateCSSVariables();
				}
			} else {
				this.isSwitched = this.defaultMode === 'dark';
				this.currentPalette = this.isSwitched ? 'dark' : 'light';
				this.updateUI();
				this.updateCSSVariables();
			}

			// Store system preference for server-side detection on future visits.
			this.setCookie( 'suredashColorSwitcherState', this.isSwitched.toString(), 90 );
		},

		/**
		 * Toggle the color palette
		 *
		 * @param {Event} e Button click event
		 */
		toggle( e ) {
			e?.preventDefault();

			// Toggle the state.
			this.isSwitched = ! this.isSwitched;
			this.currentPalette = this.isSwitched ? 'dark' : 'light';

			// Store state in cookie (expires in 90 days)
			this.setCookie( 'suredashColorSwitcherState', this.isSwitched.toString(), 90 );

			// Update UI immediately.
			this.updateUI();

			// Update CSS variables.
			this.updateCSSVariables();

			// Update body class.
			this.updateBodyClass();

			// Trigger custom event for other scripts
			document.dispatchEvent( new CustomEvent( 'suredashColorSwitcherChanged', {
				detail: {
					palette: this.currentPalette,
					isSwitched: this.isSwitched,
				},
			} ) );

			// Force reload if configured.
			if ( this.forceReload ) {
				setTimeout( () => {
					window.location.reload();
				}, 100 );
			}
		},

		/**
		 * Update CSS variables for immediate visual feedback
		 */
		updateCSSVariables() {
			// Only proceed if current palette is light or dark
			if ( this.currentPalette !== 'light' && this.currentPalette !== 'dark' ) {
				return;
			}

			const paletteColors = this.getPaletteColors( this.currentPalette );

			if ( ! paletteColors || paletteColors.length < 9 ) {
				return;
			}

			const root = document.documentElement;

			// Update the 9 global color variables.
			for ( let i = 0; i < 9; i++ ) {
				if ( paletteColors[ i ] ) {
					root.style.setProperty( `--portal-global-color-${ i + 1 }`, paletteColors[ i ] );
				}
			}
		},

		/**
		 * Update body class for palette
		 */
		updateBodyClass() {
			const body = document.body;

			// Remove existing palette classes
			body.classList.remove( 'palette-light', 'palette-dark', 'light-mode', 'dark-mode' );

			// Add new palette class
			body.classList.add( `palette-${ this.currentPalette }`, `${ this.currentPalette }-mode` );
		},

		/**
		 * Get palette colors from portal_global data
		 *
		 * @param {string} palette The palette name
		 * @return {Array} Array of color values
		 */
		getPaletteColors( palette ) {
			// Only allow light or dark palettes.
			if ( palette !== 'light' && palette !== 'dark' ) {
				return null;
			}

			// Get from portal_global data.
			if ( typeof portal_global !== 'undefined' &&
				portal_global.color_palette &&
				portal_global.color_palette[ palette ] ) {
				return portal_global.color_palette[ palette ];
			}

			// Fallback default palettes (only for light/dark).
			const defaultPalettes = {
				light: [
					'#0085FF', '#0177E3', '#FFFFFF', '#E7F6FF', '#212A37',
					'#0F172A', '#4F5B62', '#070614', '#222222',
				],
				dark: [
					'#0085FF', '#0177E3', '#1F2937', '#111827', '#F9FAFB',
					'#E5E7EB', '#9CA3AF', '#F3F4F6', '#FFFFFF',
				],
			};

			return defaultPalettes[ palette ] || null;
		},

		/**
		 * Update the UI elements (icons and text)
		 */
		updateUI() {
			this.switcherButtons.forEach( ( button ) => {
				const wrapper = button.closest( '.suredash-color-switcher-wrapper' );

				if ( wrapper ) {
					// Update wrapper class
					if ( this.isSwitched ) {
						wrapper.classList.add( 'is-switched' );
					} else {
						wrapper.classList.remove( 'is-switched' );
					}

					// Update icon.
					this.switchIcon( button );

					// Update text.
					this.switchText( button );

					// Update aria-label.
					this.updateAriaLabel( button, wrapper );
				}
			} );
		},

		/**
		 * Switch the icon
		 *
		 * @param {Element} button The button element
		 */
		switchIcon( button ) {
			const lightIconElement = button.querySelector( '.icon-light' );
			const darkIconElement = button.querySelector( '.icon-dark' );

			if ( ! lightIconElement || ! darkIconElement ) {
				return;
			}

			// Toggle visibility based on current state
			if ( this.isSwitched ) {
				// Dark mode - show dark icon, hide light icon
				lightIconElement.classList.remove( 'sd-inline-flex' );
				lightIconElement.classList.add( 'sd-display-none' );
				darkIconElement.classList.remove( 'sd-display-none' );
				darkIconElement.classList.add( 'sd-inline-flex' );
			} else {
				// Light mode - show light icon, hide dark icon
				lightIconElement.classList.remove( 'sd-display-none' );
				lightIconElement.classList.add( 'sd-inline-flex' );
				darkIconElement.classList.remove( 'sd-inline-flex' );
				darkIconElement.classList.add( 'sd-display-none' );
			}
		},

		/**
		 * Switch the text
		 *
		 * @param {Element} button The button element
		 */
		switchText( button ) {
			const lightTextElement = button.querySelector( '.text-light' );
			const darkTextElement = button.querySelector( '.text-dark' );

			if ( ! lightTextElement || ! darkTextElement ) {
				return;
			}

			// Toggle visibility based on current state
			// Note: Text logic is inverted - show opposite mode text to indicate what will happen on click
			if ( this.isSwitched ) {
				// Dark mode - show light text (indicating switch to light)
				lightTextElement.classList.remove( 'sd-display-none' );
				lightTextElement.classList.add( 'sd-inline-flex' );
				darkTextElement.classList.remove( 'sd-inline-flex' );
				darkTextElement.classList.add( 'sd-display-none' );
			} else {
				// Light mode - show dark text (indicating switch to dark)
				lightTextElement.classList.remove( 'sd-inline-flex' );
				lightTextElement.classList.add( 'sd-display-none' );
				darkTextElement.classList.remove( 'sd-display-none' );
				darkTextElement.classList.add( 'sd-inline-flex' );
			}
		},

		/**
		 * Update aria-label for accessibility
		 *
		 * @param {Element} button  The button element
		 * @param {Element} wrapper The wrapper element
		 */
		updateAriaLabel( button, wrapper ) {
			const lightText = wrapper.dataset.lightText || 'Light Mode';
			const darkText = wrapper.dataset.darkText || 'Dark Mode';
			const targetText = this.isSwitched ? lightText : darkText;

			button.setAttribute( 'aria-label', `Switch to ${ targetText }` );
		},

		/**
		 * Set cookie
		 *
		 * @param {string} name  Cookie name
		 * @param {string} value Cookie value
		 * @param {number} days  Expiry days
		 */
		setCookie( name, value, days ) {
			const expires = new Date();
			expires.setTime( expires.getTime() + ( days * 24 * 60 * 60 * 1000 ) );
			document.cookie = `${ name }=${ value };expires=${ expires.toUTCString() };path=/;SameSite=Lax`;
		},

		/**
		 * Get cookie
		 *
		 * @param {string} name Cookie name
		 * @return {string|null} Cookie value
		 */
		getCookie( name ) {
			const value = `; ${ document.cookie }`;
			const parts = value.split( `; ${ name }=` );
			if ( parts.length === 2 ) {
				return parts.pop().split( ';' ).shift();
			}
			return null;
		},
	};

	/**
	 * Initialize when DOM is ready
	 *
	 * @param {Function} fn Callback function to execute when DOM is ready
	 * @return {void}
	 */
	function domReady( fn ) {
		if ( document.readyState === 'loading' ) {
			document.addEventListener( 'DOMContentLoaded', fn );
		} else {
			fn();
		}
	}

	// Initialize the color switcher.
	domReady( () => {
		SureDashColorSwitcher.init();
	} );

	// Re-initialize on partial content updates (for AJAX/dynamic content).
	document.addEventListener( 'portal_partial_content_rendered', () => {
		SureDashColorSwitcher.isInit = false;
		SureDashColorSwitcher.init();
	} );

	// Export for global access.
	window.SureDashColorSwitcher = SureDashColorSwitcher;
}() );
